import { useState, type FormEvent } from 'react';
import { Wallet, Mail, Lock, User as UserIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { signIn, signUp, firebaseErrorToAr } from '@/firebase/auth';
import { useToast } from '@/hooks/useToast';

export function AuthScreen() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { show } = useToast();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      show('من فضلك ادخل الإيميل وكلمة المرور', 'warning');
      return;
    }
    if (mode === 'register' && password.length < 6) {
      show('كلمة المرور لازم 6 حروف على الأقل', 'warning');
      return;
    }
    setLoading(true);
    try {
      if (mode === 'register') {
        await signUp(email.trim(), password, name.trim() || undefined);
        show('تم إنشاء الحساب بنجاح');
      } else {
        await signIn(email.trim(), password);
        show('تم تسجيل الدخول');
      }
    } catch (e: any) {
      show(firebaseErrorToAr(e), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-indigo-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="w-full max-w-md animate-slide-up">
        <div className="text-center mb-8">
          <div className="relative inline-flex w-20 h-20 rounded-3xl gradient-primary items-center justify-center shadow-2xl shadow-indigo-500/40 animate-float">
            <Wallet size={36} className="text-white" />
            <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-400 border-2 border-white animate-pulse-soft" />
          </div>
          <h1 className="mt-5 text-3xl font-black gradient-text">نظام حساب المرتبات</h1>
          <p className="text-slate-600 dark:text-slate-400 text-sm mt-2">
            {mode === 'login' ? 'سجل دخولك للمتابعة' : 'أنشئ حساب جديد للبدء'}
          </p>
        </div>

        <div className="glass rounded-3xl p-7 shadow-2xl">
          <div className="grid grid-cols-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl mb-6">
            <button
              type="button"
              onClick={() => setMode('login')}
              className={`py-2 rounded-lg text-sm font-bold transition-all ${
                mode === 'login'
                  ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow'
                  : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              تسجيل دخول
            </button>
            <button
              type="button"
              onClick={() => setMode('register')}
              className={`py-2 rounded-lg text-sm font-bold transition-all ${
                mode === 'register'
                  ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow'
                  : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              حساب جديد
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div className="relative">
                <UserIcon size={16} className="absolute right-3 top-10 text-slate-400 pointer-events-none" />
                <Input
                  label="الاسم (اختياري)"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="مثلاً: محمد أحمد"
                  className="pr-10"
                />
              </div>
            )}
            <div className="relative">
              <Mail size={16} className="absolute right-3 top-10 text-slate-400 pointer-events-none" />
              <Input
                label="البريد الإلكتروني"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className="pr-10"
                dir="ltr"
                required
              />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute right-3 top-10 text-slate-400 pointer-events-none" />
              <Input
                label="كلمة المرور"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pr-10"
                dir="ltr"
                required
              />
              {mode === 'register' && (
                <p className="text-xs text-slate-500 mt-1">لازم 6 حروف على الأقل</p>
              )}
            </div>
            <Button type="submit" fullWidth size="lg" loading={loading}>
              {!loading && (mode === 'login' ? 'تسجيل الدخول' : 'إنشاء حساب')}
              {loading && <Loader2 size={16} className="animate-spin" />}
            </Button>
          </form>

          <div className="text-center text-xs text-slate-500 dark:text-slate-400 mt-6">
            بتسجيل الدخول فأنت توافق على شروط الاستخدام
          </div>
        </div>

        <div className="text-center text-[11px] text-slate-400 mt-4">
          الإصدار 2.0 • مطابق للمواصفات الأصلية • 2026
        </div>
      </div>
    </div>
  );
}
